import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import GridItem from "../KycForm/GridItem";
import FieldHeader from "../KycForm/FieldHeader";
import SelectFieldComponent from "../common/SelectFieldComponent";
import TextFieldComponent from "../KycForm/TextFieldComponent";
import DateComponent from "../common/DateComponent";
import DocumentUploads from "../KycForm/CorporateDocumentUploads";
import SubmitComponent from "../KycForm/SubmitComponent";
import OtherDocumentsComponent from "../KycForm/OtherDocumentsComponent";
import { IDENTITYTYPES } from "../../utils/constants";

const selectFieldWidth = "16.75rem";

const CorporateForm = ({
	corporateFormFields,
	setCorporateFormFields,
	countries,
	countryStates,
	cities,
	handleSubmit,
	loading,
}) => {
	const handleChange = (e) => {
		const { name, value } = e.target;
		const currentValues = { ...corporateFormFields };
		currentValues[name] = value;
		setCorporateFormFields(currentValues);
	};

	const handleImageChange = (e) => {
		const { name, files } = e.target;
		const reader = new FileReader();
		const file = files[0];
		if (file.size > 500000) {
			toast.error("file is too large");
			return;
		}
		if (
			file.type !== "image/jpg" &&
			file.type !== "image/png" &&
			file.type !== "image/jpeg"
		) {
			toast.error("Image must be of format jpg, png or jpeg");
			return;
		}
		reader.onloadend = () => {
			if (name === "passport") {
				setCorporateFormFields({
					...corporateFormFields,
					passportFile: file,
					passportUrl: reader.result,
					passportFilename: file.name,
				});
			}
			if (name === "signature") {
				setCorporateFormFields({
					...corporateFormFields,
					signatureFile: file,
					signatureUrl: reader.result,
					signatureFilename: file.name,
				});
			}
			if (name === "identificationDocument") {
				setCorporateFormFields({
					...corporateFormFields,
					identificationFile: file,
					identificationUrl: reader.result,
					identificationFilename: file.name,
				});
			}
			// second set of files
			if (name === "passportTwo") {
				setCorporateFormFields({
					...corporateFormFields,
					passportFileTwo: file,
					passportUrlTwo: reader.result,
					passportFilenameTwo: file.name,
				});
			}
			if (name === "signatureTwo") {
				setCorporateFormFields({
					...corporateFormFields,
					signatureFileTwo: file,
					signatureUrlTwo: reader.result,
					signatureFilenameTwo: file.name,
				});
			}
			if (name === "identificationTwo") {
				setCorporateFormFields({
					...corporateFormFields,
					identificationFileTwo: file,
					identificationUrlTwo: reader.result,
					identificationFilenameTwo: file.name,
				});
			}
		};

		reader.readAsDataURL(file);
	};

	const handleOtherDocumentsChange = (e) => {
		e.preventDefault();
		const reader = new FileReader();
		const file = e.target.files[0];
		if (file.size > 2000000) {
			toast.error("file is too large");
			return;
		}
		if (
			file.type !== "image/jpg" &&
			file.type !== "image/png" &&
			file.type !== "image/jpeg" &&
			file.type !== "application/pdf"
		) {
			toast.error("Image must be of format jpg, png, jpeg or pdf");
			return;
		}
		reader.onloadend = () => {
			setCorporateFormFields({
				...corporateFormFields,
				otherDocumentsFile: file,
				otherDocumentsUrl: reader.result,
				otherDocumentsFilename: file.name,
			});
		};

		reader.readAsDataURL(file);
	};

	return (
		<Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
			<Box sx={{ marginBottom: "3rem" }}>
				<FieldHeader
					header="Basic Details"
					subHeader="Update your account information"
				/>
				<Box>
					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<TextFieldComponent
									width="100%"
									value={corporateFormFields.companyName}
									onChange={handleChange}
									label="Company Name"
									id="companyName"
									name="companyName"
									required
								/>
							</Grid>
						</Grid>
					</Box>

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.email}
									onChange={handleChange}
									label="Email"
									id="email"
									name="email"
									required
								/>
							</GridItem>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.phone}
									onChange={handleChange}
									label="Phone"
									id="phone"
									name="phone"
									required
								/>
							</GridItem>
						</Grid>
					</Box>

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.secondaryPhone}
									onChange={handleChange}
									label="Secondary Phone"
									id="secondaryPhone"
									name="secondaryPhone"
								/>
							</GridItem>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.chn}
									onChange={handleChange}
									label="Clearing House Number (CHN)"
									id="chn"
									name="chn"
								/>
							</GridItem>
						</Grid>
					</Box>

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.previousAddress}
									onChange={handleChange}
									label="Previous Address (If Any)"
									id="previousAddress"
									name="previousAddress"
								/>
							</GridItem>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.addressOne}
									onChange={handleChange}
									label="Address Line 1 *"
									id="addressOne"
									name="addressOne"
								/>
							</GridItem>
						</Grid>
					</Box>

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<GridItem>
								<TextFieldComponent
									width={selectFieldWidth}
									value={corporateFormFields.addressTwo}
									onChange={handleChange}
									label="Address Line 2"
									id="addressTwo"
									name="addressTwo"
								/>
							</GridItem>
							<GridItem>
								<SelectFieldComponent
									width={selectFieldWidth}
									values={countries}
									value={corporateFormFields.country}
									onChange={handleChange}
									label="Address Country"
									id="country"
									name="country"
									required
									menuValue="name"
									displayValue="name"
								/>
							</GridItem>
						</Grid>
					</Box>

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<GridItem>
								<SelectFieldComponent
									width={selectFieldWidth}
									values={countryStates}
									value={corporateFormFields.state}
									onChange={handleChange}
									label="Address State"
									id="state"
									name="state"
									required
									menuValue="name"
									displayValue="name"
								/>
							</GridItem>
							<GridItem>
								<SelectFieldComponent
									width={selectFieldWidth}
									values={cities}
									value={corporateFormFields.city}
									onChange={handleChange}
									label="Address City"
									id="city"
									name="city"
									required
									menuValue="name"
									displayValue="name"
								/>
							</GridItem>
						</Grid>
					</Box>

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<SelectFieldComponent
									width="100%"
									values={IDENTITYTYPES}
									value={corporateFormFields.identityTypeOne}
									onChange={handleChange}
									label="Identity Type 1"
									id="identityTypeOne"
									name="identityTypeOne"
									required
								/>
							</Grid>
						</Grid>
					</Box>

					{corporateFormFields.identityTypeOne !== "Others" && (
						<>
							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<TextFieldComponent
											width={selectFieldWidth}
											value={corporateFormFields.identityNumberOne}
											onChange={handleChange}
											label="Identity Number 1"
											id="identityNumberOne"
											name="identityNumberOne"
										/>
									</GridItem>
									<GridItem>
										<DateComponent
											value={corporateFormFields.identityExpiryDateOne}
											onChange={(newValue) =>
												setCorporateFormFields({
													...corporateFormFields,
													identityExpiryDateOne: newValue,
												})
											}
											width={selectFieldWidth}
											label="Identity Expiry Date 1"
											required
											disablePast
										/>
									</GridItem>
								</Grid>
							</Box>
						</>
					)}

					{corporateFormFields.identityTypeOne === "Others" && (
						<>
							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<TextFieldComponent
											width={selectFieldWidth}
											value={corporateFormFields.enterIdentityTypeOne}
											onChange={handleChange}
											label="Enter Identity Type 1"
											id="enterIdentityTypeOne"
											name="enterIdentityTypeOne"
										/>
									</GridItem>
									<GridItem>
										<TextFieldComponent
											width={selectFieldWidth}
											value={corporateFormFields.identityNumberOne}
											onChange={handleChange}
											label="Identity Number 1"
											id="identityNumberOne"
											name="identityNumberOne"
										/>
									</GridItem>
								</Grid>
							</Box>

							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<DateComponent
											value={corporateFormFields.identityExpiryDateOne}
											onChange={(newValue) =>
												setCorporateFormFields({
													...corporateFormFields,
													identityExpiryDateOne: newValue,
												})
											}
											width={selectFieldWidth}
											label="Identity Expiry Date 1"
											required
											disablePast
										/>
									</GridItem>
								</Grid>
							</Box>
						</>
					)}

					<Box
						sx={{
							backgroundColor: "#fff",
							padding: "1.5rem",
						}}
					>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<SelectFieldComponent
									width="100%"
									values={IDENTITYTYPES}
									value={corporateFormFields.identityTypeTwo}
									onChange={handleChange}
									label="Identity Type 2"
									id="identityTypeTwo"
									name="identityTypeTwo"
								/>
							</Grid>
						</Grid>
					</Box>

					{corporateFormFields.identityTypeTwo !== "Others" && (
						<>
							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<TextFieldComponent
											width={selectFieldWidth}
											value={corporateFormFields.identityNumberTwo}
											onChange={handleChange}
											label="Identity Number 2"
											id="identityNumberTwo"
											name="identityNumberTwo"
										/>
									</GridItem>
									<GridItem>
										<DateComponent
											value={corporateFormFields.identityExpiryDateTwo}
											onChange={(newValue) =>
												setCorporateFormFields({
													...corporateFormFields,
													identityExpiryDateTwo: newValue,
												})
											}
											width={selectFieldWidth}
											label="Identity Expiry Date 2"
											disablePast
										/>
									</GridItem>
								</Grid>
							</Box>
						</>
					)}

					{corporateFormFields.identityTypeTwo === "Others" && (
						<>
							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<TextFieldComponent
											width={selectFieldWidth}
											value={corporateFormFields.enterIdentityTypeTwo}
											onChange={handleChange}
											label="Enter Identity Type 2"
											id="enterIdentityTypeTwo"
											name="enterIdentityTypeTwo"
										/>
									</GridItem>
									<GridItem>
										<TextFieldComponent
											width={selectFieldWidth}
											value={corporateFormFields.identityNumberTwo}
											onChange={handleChange}
											label="Identity Number 2"
											id="identityNumberTwo"
											name="identityNumberTwo"
										/>
									</GridItem>
								</Grid>
							</Box>

							<Box
								sx={{
									backgroundColor: "#fff",
									padding: "1.5rem",
								}}
							>
								<Grid container spacing={2}>
									<GridItem>
										<DateComponent
											value={corporateFormFields.identityExpiryDateTwo}
											onChange={(newValue) =>
												setCorporateFormFields({
													...corporateFormFields,
													identityExpiryDateTwo: newValue,
												})
											}
											width={selectFieldWidth}
											label="Identity Expiry Date 2"
											disablePast
										/>
									</GridItem>
								</Grid>
							</Box>
						</>
					)}
				</Box>
			</Box>

			<Box sx={{ marginBottom: "3rem" }}>
				<FieldHeader
					header="Document Uploads"
					subHeader="Upload your signature mandate, passport photograph, utility bill and means of identification. Only images in jpg, jpeg, png format and max size 500kb are accepted."
				/>
				<DocumentUploads
					handleChange={handleImageChange}
					passportUrl={corporateFormFields.passportUrl}
					passportFileSize={corporateFormFields.passportFile.size}
					signatureUrl={corporateFormFields.signatureUrl}
					signatureFileSize={corporateFormFields.signatureFile.size}
					identificationUrl={corporateFormFields.identificationUrl}
					identificationFileSize={corporateFormFields.identificationFile.size}
					passportUrlTwo={corporateFormFields.passportUrlTwo}
					passportFileSizeTwo={corporateFormFields.passportFileTwo.size}
					signatureUrlTwo={corporateFormFields.signatureUrlTwo}
					signatureFileSizeTwo={corporateFormFields.signatureFileTwo.size}
					identificationUrlTwo={corporateFormFields.identificationUrlTwo}
					identificationFileSizeTwo={
						corporateFormFields.identificationFileTwo.size
					}
				/>
			</Box>
			<OtherDocumentsComponent
				handleChange={handleOtherDocumentsChange}
				url={corporateFormFields.otherDocumentsUrl}
				filename={corporateFormFields.otherDocumentsFilename}
				fileSize={corporateFormFields.otherDocumentsFile.size}
				fileType={corporateFormFields.otherDocumentsFile.type}
			/>
			<SubmitComponent loading={loading} />
		</Box>
	);
};

export default CorporateForm;
